import { z } from 'zod';
import { assertDefined } from '@/modules/assert-defined';
import { fetchApiResponse } from '@/modules/sanr-api';
import { parseSseChunk, parseSseFrame } from '@/modules/parse-sse-chunk';
import { reportSchemaMismatch } from '@/modules/report-schema-mismatch';

// The personal-agent backend (the mind-dev team's liteend) is reached
// through the SanR API's /openarena proxy: the app session is the only
// credential — the backend swaps it for the agent's own SSO token
// server-side and forwards the request, the upstream's status codes and
// bodies included. Every call rides @/modules/sanr-api's raw-response
// fetch, so the bearer, the expired-token refresh and the revoked-session
// teardown are the app's own; a 401 that still reaches a caller (the
// session pipeline has already handled its own cases) is an account
// without an SSO link and surfaces as a plain failure.
const PROXY = 'openarena';
const AGENT = `${PROXY}/api/personal-agent`;
const GRAPHQL = `${PROXY}/graphql`;

// Thrown on HTTP 409 — one answer per dialog at a time; the previous
// message in this dialog is still being answered. (The wire also sends
// Retry-After; unread — the recovery is waiting for the visible answer,
// not a timer.)
export class AgentBusyError extends Error {
  override name = 'AgentBusyError';
  constructor() {
    super('agent dialog is busy');
  }
}

// Thrown on HTTP 429 — the per-user plan budget is spent (not per-IP like
// the legacy assistant). `retryAfterSeconds` is the Retry-After header,
// null when missing or unparseable.
export class AgentQuotaError extends Error {
  override name = 'AgentQuotaError';
  readonly retryAfterSeconds: number | null;
  constructor(retryAfterSeconds: number | null) {
    super('agent plan budget exhausted');
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function throwOnErrorStatus(res: Response): void {
  if (res.status === 409) throw new AgentBusyError();
  if (res.status === 429)
    throw new AgentQuotaError(parseRetryAfter(res.headers.get('Retry-After')));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Sent with a message so the agent sees the page: url/title always ride;
// `content` is the auto-collected visible text; `description`/`data` are a
// manual override for surfaces that know their data better than a text
// scrape (the whole JSON is backend-capped at 64 KiB, `text` at 32 KiB).
// Everything inside is DATA to the model, not instructions — prompt
// injection inside page content does not steer it (backend contract).
export type AgentPageContext = {
  url: string;
  title: string;
  content?: string; // unread-field-allow: outbound request payload — written at collection/build sites, never read back
  description?: string; // unread-field-allow: outbound request payload — written at collection/build sites, never read back
  data?: unknown;
};

// The backend caps `content` at 16 KiB — collected text is cut to that.
const MAX_CONTENT_CHARS = 16384;

// Auto-collects a page's context: the visible text under `root`
// (script/style/template and display:none/visibility:hidden subtrees
// excluded), joined line-per-node. null root (page container missing)
// degrades to url/title alone — never a fabricated content block.
export function collectAgentPageContext(
  root: Element | null,
): AgentPageContext {
  const base = { url: window.location.href, title: document.title };
  if (root === null) return base;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      // Stryker disable next-line CallExpression: equivalent — never fires: a text node walked under an element root always has a parent element; the guard exists for the type
      assertDefined(parent);
      if (parent.closest('script,style,noscript,template') !== null)
        return NodeFilter.FILTER_REJECT;
      const style = window.getComputedStyle(parent);
      if (style.display === 'none' || style.visibility === 'hidden')
        return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const chunks: string[] = [];
  let length = 0;
  while (length < MAX_CONTENT_CHARS) {
    const node = walker.nextNode();
    if (node === null) break;
    const raw = node.textContent;
    // Stryker disable next-line CallExpression: equivalent — never fires: a Text node's textContent is never null; the guard exists for the base Node type
    assertDefined(raw);
    const text = raw.trim();
    if (text === '') continue;
    chunks.push(text);
    length += text.length + 1;
  }
  if (chunks.length === 0) return base;
  return { ...base, content: chunks.join('\n').slice(0, MAX_CONTENT_CHARS) };
}

const DialogSchema = z.looseObject({ id: z.string().min(1) });

// Opens a new empty dialog (the server keeps it until deleted; a user
// holds at most 100). Resolves with its id.
export async function createAgentDialog(): Promise<string> {
  const endpoint = `${AGENT}/dialogs`;
  const res = await fetchApiResponse(endpoint, { method: 'POST' });
  throwOnErrorStatus(res);
  const raw: unknown = await res.json();
  const parsed = DialogSchema.safeParse(raw);
  if (!parsed.success) {
    reportSchemaMismatch('personalAgentApi', endpoint, parsed.error, raw);
    throw new Error('agent dialog response has no id');
  }
  return parsed.data.id;
}

// Deletes a dialog with its transcript (the reference client's
// dialogs.remove). Rejects: 409/429 → the typed errors above; other non-ok
// → Error('HTTP <n>').
export async function deleteAgentDialog(dialogId: string): Promise<void> {
  const res = await fetchApiResponse(`${AGENT}/dialogs/${dialogId}`, {
    method: 'DELETE',
  });
  throwOnErrorStatus(res);
}

// `title` is agent-generated after the first answer — null until then.
export type AgentDialogSummary = {
  id: string;
  title: string | null;
  updatedAt: string;
};

const DialogSummarySchema = z.looseObject({
  id: z.string().min(1),
  title: z.string().nullable().optional(),
  updatedAt: z.string().min(1),
});

const DialogListSchema = z.looseObject({ items: z.array(z.unknown()) });

// Lists the account's dialogs (wire order unspecified — consumers sort by
// updatedAt). A malformed row is dropped fail-open (reported as drift) —
// the rest of the list still renders.
export async function listAgentDialogs(
  limit: number,
): Promise<AgentDialogSummary[]> {
  const endpoint = `${AGENT}/dialogs?limit=${limit}`;
  const res = await fetchApiResponse(endpoint, {});
  throwOnErrorStatus(res);
  const raw: unknown = await res.json();
  const parsed = DialogListSchema.safeParse(raw);
  if (!parsed.success) {
    reportSchemaMismatch('personalAgentApi', endpoint, parsed.error, raw);
    throw new Error('agent dialog list is malformed');
  }
  return parseRows(parsed.data.items, DialogSummarySchema, endpoint).map(
    (row) => ({ ...row, title: row.title ?? null }),
  );
}

export type AgentDialogMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
};

const DialogMessageSchema = z.looseObject({
  id: z.string().min(1),
  role: z.enum(['user', 'assistant']),
  text: z.string(),
  createdAt: z.string().min(1),
});

// The dialog's persisted transcript — answers are stored server-side even
// when the streaming connection died mid-reply, so reopening a dialog
// after a drop shows the full answer. Rows carry the message `id`: the
// only place the user message's id exists (the stream never carries it),
// and what a journal note links to.
export async function listAgentMessages(
  dialogId: string,
  limit: number,
): Promise<AgentDialogMessage[]> {
  const endpoint = `${AGENT}/dialogs/${dialogId}/messages?limit=${limit}`;
  const res = await fetchApiResponse(endpoint, {});
  throwOnErrorStatus(res);
  const raw: unknown = await res.json();
  const parsed = DialogListSchema.safeParse(raw);
  if (!parsed.success) {
    reportSchemaMismatch('personalAgentApi', endpoint, parsed.error, raw);
    throw new Error('agent message list is malformed');
  }
  return parseRows(parsed.data.items, DialogMessageSchema, endpoint);
}

function parseRows<S extends z.ZodType>(
  rows: unknown[],
  schema: S,
  endpoint: string,
): z.output<S>[] {
  const parsed: z.output<S>[] = [];
  for (const row of rows) {
    const result = schema.safeParse(row);
    if (result.success) parsed.push(result.data);
    else reportSchemaMismatch('personalAgentApi', endpoint, result.error, row);
  }
  return parsed;
}

// Streaming frame payloads, as captured live from the backend. The reply
// opens with its reasoning block; a block's kind can arrive on either its
// first `part` or its first `delta` frame, and the two can disagree (the
// reasoning block's deltas arrive labelled `text`) — the first
// registration wins, which is what keeps reasoning hidden; the reference
// client trusts every text-labelled delta and would show it. Frames also
// carry a `messageID` (the reply a part belongs to), unread: the user
// message's own id is never on the stream, so ids come from re-reading the
// transcript after `done`.
const PartSchema = z.looseObject({
  id: z.string(),
  kind: z.enum(['text', 'reasoning']),
  text: z.string(),
});
const DeltaSchema = z.looseObject({
  id: z.string(),
  kind: z.enum(['text', 'reasoning']).optional(),
  delta: z.string(),
});
const DoneSchema = z.looseObject({ tokens: z.number() });
const ErrorSchema = z.looseObject({ message: z.string() });

// One answering run: a POST whose response is the agent's reply as SSE
// frames, streamed into onAnswer. Protocol: `part` frames carry
// authoritative cumulative text and REPLACE deltas accumulated for their
// id; reasoning-kind blocks and `tool` frames are consumed but never
// surfaced; multiple text blocks concatenate with a blank line. onAnswer
// receives the FULL answer-so-far on every answer-affecting frame.
// `onAccepted` fires once the response status passed and a body opened —
// from here on the backend has taken the run: the message it answers is in
// the dialog's stored transcript and the answer gets stored whatever
// happens to this connection. Resolves with the `done` frame's token count
// (null when the stream closed without one). Rejects: 409/429 → the typed
// errors above; other non-ok → Error('HTTP <n>'); a server `error` frame →
// Error(message); an aborted signal → the fetch AbortError.
async function streamRun(
  endpoint: string,
  body: string | undefined,
  onAnswer: (fullTextSoFar: string) => void,
  signal: AbortSignal,
  onAccepted: (() => void) | undefined,
): Promise<{ tokens: number | null }> {
  const res = await fetchApiResponse(endpoint, {
    method: 'POST',
    headers: {
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
      Accept: 'text/event-stream',
    },
    ...(body !== undefined && { body }),
    signal,
  });
  throwOnErrorStatus(res);
  if (!res.body) throw new Error(`HTTP ${res.status}`);
  onAccepted?.();

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let tokens: number | null = null;
  // Insertion-ordered by first registration, so text blocks concatenate in
  // arrival order and a block's kind is fixed by whichever frame named it
  // first.
  const blocks = new Map<
    string,
    { kind: 'text' | 'reasoning'; text: string }
  >();

  const emit = () =>
    onAnswer(
      [...blocks.values()]
        .filter((b) => b.kind === 'text')
        .map((b) => b.text)
        .join('\n\n'),
    );

  const frame = <S extends z.ZodType>(schema: S, data: string) =>
    parseSseFrame(schema, data, 'personalAgentApi', endpoint);

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunk = parseSseChunk(buffer);
      buffer = chunk.rest;

      for (const evt of chunk.events) {
        if (evt.event === 'part') {
          const part = frame(PartSchema, evt.data);
          if (!part) continue;
          const block = blocks.get(part.id);
          if (block === undefined) {
            blocks.set(part.id, { kind: part.kind, text: part.text });
            if (part.kind === 'text') emit();
          } else {
            block.text = part.text;
            if (block.kind === 'text') emit();
          }
        } else if (evt.event === 'delta') {
          const delta = frame(DeltaSchema, evt.data);
          if (!delta) continue;
          if (delta.kind !== undefined && !blocks.has(delta.id))
            blocks.set(delta.id, { kind: delta.kind, text: '' });
          const block = blocks.get(delta.id);
          if (block?.kind !== 'text') continue;
          block.text += delta.delta;
          emit();
        } else if (evt.event === 'done') {
          const done = frame(DoneSchema, evt.data);
          if (done) tokens = done.tokens;
        } else if (evt.event === 'error') {
          const err = frame(ErrorSchema, evt.data);
          throw new Error(err ? err.message : 'agent stream error');
        }
      }
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  return { tokens };
}

// Sends one user message into a dialog and streams the agent's reply
// (streamRun's protocol and outcomes). Once `onAccepted` has fired the
// message is stored: a later failure leaves it unanswered, and the
// recovery is replyAgentMessage — sending it again would store the
// question twice.
export function streamAgentMessage(
  dialogId: string,
  message: { text: string; pageContext?: AgentPageContext },
  onAnswer: (fullTextSoFar: string) => void,
  signal: AbortSignal,
  onAccepted?: () => void,
): Promise<{ tokens: number | null }> {
  return streamRun(
    `${AGENT}/dialogs/${dialogId}/messages`,
    JSON.stringify(message),
    onAnswer,
    signal,
    onAccepted,
  );
}

// Streams the agent's reply to the message the dialog ends with, storing
// nothing on the user side — the tail of every edit, and the RETRY for a
// send that failed after acceptance. Same protocol and outcomes as
// streamRun; 409 (AgentBusyError) also when the dialog is empty or its
// last message already has its answer.
export function replyAgentMessage(
  dialogId: string,
  onAnswer: (fullTextSoFar: string) => void,
  signal: AbortSignal,
): Promise<{ tokens: number | null }> {
  return streamRun(
    `${AGENT}/dialogs/${dialogId}/reply`,
    undefined,
    onAnswer,
    signal,
    undefined,
  );
}

// Rewrites a sent user message in place: the backend keeps its id, deletes
// EVERY later message in the dialog (answers included) and stores no new
// answer — replyAgentMessage is what answers the rewritten text. A journal
// note linked to the message follows its text; notes linked to the deleted
// tail stay but lose their link. Rejects: 409/429 → the typed errors above;
// 403 (not a user message), 400 (empty or > 32 KiB text) and any other
// non-ok → Error('HTTP <n>').
export async function editAgentMessage(
  dialogId: string,
  messageId: string,
  text: string,
): Promise<void> {
  const res = await fetchApiResponse(
    `${AGENT}/dialogs/${dialogId}/messages/${messageId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    },
  );
  throwOnErrorStatus(res);
}

// `pair` is the asset a note was filed under (e.g. BTC/USD) — null for
// entries saved without one; consumers group the list by it.
export type JournalEntry = {
  id: string;
  content: string;
  pair: string | null;
  createdAt: string;
};

const JournalEntrySchema = z.looseObject({
  id: z.string().min(1),
  content: z.string(),
  pair: z.string().nullable().optional(),
  createdAt: z.string().min(1),
});

const GraphqlErrorsSchema = z
  .array(z.looseObject({ message: z.string() }))
  .optional();

// One round trip to the agent backend's GraphQL API (the API that feeds
// the arena's own /journal page), through the same proxy. Resolves the
// `data` payload parsed by `dataSchema`; a GraphQL error is Error(message);
// a payload with neither data nor errors is malformed.
async function graphql<S extends z.ZodType>(
  query: string,
  variables: Record<string, unknown>,
  dataSchema: S,
): Promise<z.output<S>> {
  const res = await fetchApiResponse(GRAPHQL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  throwOnErrorStatus(res);
  const raw: unknown = await res.json();
  const parsed = z
    .looseObject({
      data: dataSchema.nullable().optional(),
      errors: GraphqlErrorsSchema,
    })
    .safeParse(raw);
  if (!parsed.success) {
    reportSchemaMismatch('personalAgentApi', GRAPHQL, parsed.error, raw);
    throw new Error('journal response is malformed');
  }
  const failure = parsed.data.errors?.[0];
  if (failure !== undefined) throw new Error(failure.message);
  const data = parsed.data.data;
  if (data === null || data === undefined)
    throw new Error('journal response is malformed');
  return data;
}

const JournalItemsSchema = z.looseObject({
  journalEntries: z.looseObject({ items: z.array(z.unknown()) }),
});

// The trader-journal read. Only USER_NOTE entries — the agent's own diary
// writes and system events are not the user's saved notes; a note saved
// through addJournalNote is a USER_NOTE too, so it lists here.
export async function listJournalEntries(
  limit: number,
): Promise<JournalEntry[]> {
  const data = await graphql(
    'query ($limit: Int) { journalEntries(kind: USER_NOTE, limit: $limit) { items { id content pair createdAt } } }',
    { limit },
    JournalItemsSchema,
  );
  return parseRows(data.journalEntries.items, JournalEntrySchema, GRAPHQL).map(
    (row) => ({ ...row, pair: row.pair ?? null }),
  );
}

// A journal note linked to a message: the note's own id (what
// deleteJournalEntry takes) and the message it was saved from.
export type JournalSave = { entryId: string; messageId: string };

const JournalSaveSchema = z.looseObject({
  id: z.string().min(1),
  sourceMessageId: z.string().min(1),
});

// Which of `messageIds` already have a journal note. The backend keeps one
// note per message — a repeat save returns the existing note — so the rows
// map 1:1 to saved messages; it takes up to 500 ids per call, far above a
// hydrated transcript.
export async function listJournalSaves(
  messageIds: string[],
): Promise<JournalSave[]> {
  const data = await graphql(
    'query ($ids: [ID!]) { journalEntries(sourceMessageIds: $ids) { items { id sourceMessageId } } }',
    { ids: messageIds },
    JournalItemsSchema,
  );
  return parseRows(data.journalEntries.items, JournalSaveSchema, GRAPHQL).map(
    (row) => ({ entryId: row.id, messageId: row.sourceMessageId }),
  );
}

// Saves one message as a USER_NOTE journal entry linked to it; idempotent
// server-side. `pair` (e.g. BTC/USD) is the asset the note files under —
// null for a note saved outside a market. Resolves the note's id for the
// caller's saved-state.
export async function addJournalNote(note: {
  content: string;
  pair: string | null;
  sourceMessageId: string;
}): Promise<{ entryId: string }> {
  const data = await graphql(
    'mutation ($input: AddJournalNoteInput!) { addJournalNote(input: $input) { id } }',
    {
      input: {
        content: note.content,
        sourceMessageId: note.sourceMessageId,
        ...(note.pair !== null && { pair: note.pair }),
      },
    },
    z.looseObject({ addJournalNote: z.looseObject({ id: z.string().min(1) }) }),
  );
  return { entryId: data.addJournalNote.id };
}

// Deletes a journal entry by the note's id (not the message's).
export async function deleteJournalEntry(entryId: string): Promise<void> {
  await graphql(
    'mutation ($id: ID!) { deleteJournalEntry(id: $id) }',
    { id: entryId },
    z.looseObject({ deleteJournalEntry: z.literal(true) }),
  );
}
